/**
 * 後台 — 訂單列表(#10 Phase 1.5)
 *
 * Filter:status(全部 / 待出貨 / 已出貨 / 已完成 / 已取消)
 * Click row → /admin/orders/:id 詳情
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminDeleteOrder, adminExportOrders, adminListOrders, adminUpdateOrderStatus, ORDER_STATUS_LABELS } from "../../api/admin";
import type { Order } from "../../api/cart";
import AdminHeader from "../../components/AdminHeader";
import DataTable, { Column } from "../../components/DataTable";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpeCompact } from "../../utils/date";


export default function OrderList() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const [rows, setRows] = useState<Order[]>([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, dateFrom, dateTo, role]);

  // search debounce 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (role === "admin") refresh();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function refresh() {
    setLoading(true);
    try {
      const r = await adminListOrders({
        status: filterStatus || undefined,
        search: search.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit: 100,
      });
      setRows(r.orders);
      setSelected(new Set());     // 換 filter 清空選取,避免操作到看不到的單
    } catch {
      useUIStore.getState().showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.order_id || "")),
    );
  }

  async function bulkDelete() {
    const ids = Array.from(selected).filter(Boolean);
    if (!ids.length) return;
    // 已入帳判斷:status 非「待付(pending_payment_mock)」的單,多半已 credit 瓶數到會員存摺。
    // 硬刪訂單不會回退那些瓶數,故明確點出有幾筆 + 提醒先出帳,避免存摺留下對不到訂單的孤兒入帳。
    const selectedOrders = rows.filter((o) => ids.includes(o.order_id || ""));
    const creditedCount = selectedOrders.filter((o) => o.status && o.status !== "pending_payment_mock").length;
    const warn =
      creditedCount > 0
        ? `永久刪除勾選的 ${ids.length} 筆訂單?不可復原。\n\n` +
          `注意:其中 ${creditedCount} 筆可能已入帳到會員燕窩存摺 —— 刪除「不會」自動扣回瓶數,存摺會留下對不到訂單的入帳紀錄。\n` +
          `若要連瓶數一起清掉,請先到該會員存摺手動「出帳」,再回來刪訂單。\n\n` +
          `僅建議用於清測試 / 髒資料。`
        : `永久刪除勾選的 ${ids.length} 筆訂單?不可復原。\n(這些都是未入帳的待付單,不影響存摺。)`;
    if (!confirm(warn)) return;
    setBulkRunning(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        const r = await adminDeleteOrder(id);
        if (r.status === "ok") ok++;
        else fail++;
      } catch {
        fail++;
      }
    }
    setBulkRunning(false);
    setSelected(new Set());
    if (fail === 0) {
      useUIStore.getState().showToast(`已刪除 ${ok} 筆`, "success");
    } else {
      useUIStore.getState().showToast(`成功 ${ok} 筆,失敗 ${fail} 筆`, "error");
    }
    refresh();
  }

  async function exportExcel() {
    const ids = Array.from(selected).filter(Boolean);
    if (!ids.length) return;
    setExporting(true);
    try {
      const blob = await adminExportOrders(ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `訂單_${todayTpeCompact()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      useUIStore.getState().showToast(`已匯出 ${ids.length} 筆訂單`, "success");
    } catch {
      useUIStore.getState().showToast("匯出失敗,請再試一次", "error");
    } finally {
      setExporting(false);
    }
  }

  async function bulkUpdateStatus(targetStatus: "completed" | "cancelled") {
    const ids = Array.from(selected).filter(Boolean);
    if (!ids.length) return;
    const label = targetStatus === "completed" ? "標記已完成" : "取消";
    const extra = targetStatus === "cancelled" ? "(會推撤回通知給客戶)" : "";
    if (!confirm(`確認對勾選的 ${ids.length} 筆訂單${label}?${extra}`)) return;
    setBulkRunning(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        const r = await adminUpdateOrderStatus(id, targetStatus, "");
        if (r.status === "ok") ok++;
        else fail++;
      } catch {
        fail++;
      }
    }
    setBulkRunning(false);
    setSelected(new Set());
    if (fail === 0) {
      useUIStore.getState().showToast(`已${label} ${ok} 筆`, "success");
    } else {
      useUIStore.getState().showToast(`成功 ${ok} 筆,失敗 ${fail} 筆`, "error");
    }
    refresh();
  }

  const columns: Column<Order>[] = [
    {
      key: "order_id",
      header: "訂單編號",
      render: (r) => <span className="font-mono text-xs">#{(r.order_id || "").slice(0, 8)}</span>,
    },
    { key: "name", header: "收件人", render: (r) => r.name || "—" },
    { key: "phone", header: "電話", render: (r) => r.phone || "—" },
    {
      key: "total",
      header: "總額",
      render: (r) => `NT$ ${(r.total || 0).toLocaleString()}`,
    },
    {
      key: "status",
      header: "狀態",
      render: (r) => {
        // 空 status(舊單沒寫)→ 視為待出貨,只是顯示 fallback,不寫資料
        const s = r.status || "pending_payment_mock";
        const label = ORDER_STATUS_LABELS[s] || s;
        const color = statusColor(s);
        return <span className={`text-xs px-2 py-1 rounded ${color}`}>{label}</span>;
      },
    },
    {
      key: "created_at",
      header: "下單時間",
      render: (r) => r.created_at ? String(r.created_at).slice(0, 16).replace("T", " ") : "—",
    },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="orders" />

      <div className="p-6">
        <div className="card mb-5 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="label">搜尋</label>
            <input
              className="input w-full"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="訂單編號 / 收件人 / 電話"
            />
          </div>
          <div>
            <label className="label">狀態</label>
            <select
              className="input min-w-32"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">全部</option>
              <option value="pending_payment_mock">待出貨</option>
              <option value="partially_shipped">部分出貨</option>
              <option value="shipped">已出貨</option>
              <option value="completed">已完成</option>
              <option value="cancelled">已取消</option>
            </select>
          </div>
          <div>
            <label className="label">起日</label>
            <input
              className="input"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="label">迄日</label>
            <input
              className="input"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="ml-auto">
            <button onClick={() => navigate("/admin/orders/new")} className="btn-primary">
              ＋ 人工建單
            </button>
          </div>
        </div>

        {/* 選取工具列(勾起時冒出) */}
        {selected.size > 0 && (
          <div className="card mb-3 flex items-center gap-3 bg-sage-light/40 border border-sage">
            <div className="text-sm font-medium text-ink">
              已選 {selected.size} 筆
            </div>
            <div className="ml-auto flex gap-2">
              <button
                onClick={exportExcel}
                disabled={bulkRunning || exporting}
                className="btn-secondary text-sm disabled:opacity-40"
                title="匯出勾選的訂單(一列一張訂單,原價/折讓/實收)"
              >
                {exporting ? "匯出中…" : "匯出 Excel"}
              </button>
              <button
                onClick={() => {
                  const id = Array.from(selected)[0];
                  if (id) navigate(`/admin/orders/${id}`);
                }}
                disabled={selected.size !== 1 || bulkRunning}
                className="btn-secondary text-sm disabled:opacity-40"
                title={selected.size === 1 ? "進詳情頁編輯" : "只勾 1 筆才能用"}
              >
                編輯
              </button>
              <button
                onClick={() => bulkUpdateStatus("completed")}
                disabled={bulkRunning}
                className="btn-secondary text-sm disabled:opacity-40"
              >
                標記已完成
              </button>
              <button
                onClick={() => bulkUpdateStatus("cancelled")}
                disabled={bulkRunning}
                className="text-sm text-coral border border-coral/40 rounded px-3 py-1.5 hover:bg-coral/10 disabled:opacity-40"
              >
                取消
              </button>
              <button
                onClick={bulkDelete}
                disabled={bulkRunning}
                className="text-sm text-white bg-coral rounded px-3 py-1.5 hover:bg-coral/90 disabled:opacity-40"
                title="永久刪除,不可復原。已付款的單不會回退錢包"
              >
                刪除
              </button>
              <button
                onClick={() => setSelected(new Set())}
                disabled={bulkRunning}
                className="text-sm text-muted px-2 py-1.5 hover:text-ink disabled:opacity-40"
              >
                清除
              </button>
            </div>
          </div>
        )}

        <div className="card">
          <DataTable
            columns={columns}
            rows={rows}
            onRowClick={(r) => navigate(`/admin/orders/${r.order_id}`)}
            emptyHint={loading ? "載入中…" : "尚無訂單"}
            selectable
            getRowId={(r) => r.order_id || ""}
            selectedIds={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
          />
          <div className="text-xs text-muted mt-3 text-right">共 {rows.length} 筆</div>
        </div>
      </div>
    </div>
  );
}


function statusColor(status: string): string {
  switch (status) {
    case "pending_payment_mock":
    case "paid":
      return "bg-sage-light text-charcoal";
    case "partially_shipped":
      return "bg-sage text-white";
    case "shipped":
      return "bg-coral text-white";
    case "completed":
      return "bg-primary text-white";
    case "cancelled":
      return "bg-muted text-white";
    default:
      return "bg-cream text-charcoal";
  }
}
